import { currencySymbols, DEFAULT_CURRENCY_SYMBOL } from './constants';
import { safeNumberFormat } from '@localization/safeIntl';

const numberFormatCacheKey = (locales?: string | string[], options: Intl.NumberFormatOptions = {}) => {
  const localeKey = Array.isArray(locales) ? locales.sort().join('-') : locales;

  return `${localeKey}-${JSON.stringify(options)}`;
};

const numberFormats = new Map<string, Intl.NumberFormat>();

export const memoizedNumberFormatter = (locales?: string | string[], options?: Intl.NumberFormatOptions) => {
  const key = numberFormatCacheKey(locales, options);
  if (numberFormats.has(key)) {
    return numberFormats.get(key)!;
  }
  // safeNumberFormat sanitizes non-BCP 47 tags (e.g. "en-US@posix") and falls
  // back to en-US instead of throwing a RangeError.
  const i = safeNumberFormat(locales, options);
  numberFormats.set(key, i);
  return i;
};

export function getCurrencySymbol(locale: string, options: Intl.NumberFormatOptions) {
  // formatToParts labels the currency directly, so locales that render numbers
  // with non-Latin digits (fa, ar-EG, bn, ...) work here. The previous
  // implementation regex-hunted ASCII digits in the formatted string and threw
  // for those locales.
  const parts = memoizedNumberFormatter(locale, { style: 'currency', ...options }).formatToParts(0);
  const currencyIndex = parts.findIndex(part => part.type === 'currency');
  if (currencyIndex === -1) {
    throw new Error(`Number input in locale ${locale} is currently not supported.`);
  }
  // Keep the adjacent literals (spaces, bidi marks) with the symbol: consumers
  // concatenate symbol + amount directly, so a suffixed locale like cs-CZ
  // ("1 234,56 Kč") needs its trailing space, matching the old behavior of
  // splitting the formatted string around the amount.
  const symbol = parts
    .map((part, index) => (index === currencyIndex || ((index === currencyIndex - 1 || index === currencyIndex + 1) && part.type === 'literal') ? part.value : ''))
    .join('');
  // Position is semantic: the currency precedes the amount iff its part index
  // is below the integer's. Filtering "known whitespace" breaks when options
  // (e.g. signDisplay: 'always') insert other parts before the currency.
  const integerIndex = parts.findIndex(part => part.type === 'integer');

  return {
    symbol,
    prefixed: currencyIndex < integerIndex,
  };
}

export function getCurrencySymbolFromCurrencyCode(currencyCode?: string | null): string {
  const code = currencyCode?.toUpperCase() as keyof typeof currencySymbols;

  if (!code || !Object.prototype.hasOwnProperty.call(currencySymbols, code)) {
    return DEFAULT_CURRENCY_SYMBOL;
  }

  return currencySymbols[code];
}

export function getCharmPriceForQuantity(_floatingPointDecimalString: string, quantity: number, amountInMajorUnits: number): number {
  return amountInMajorUnits * quantity;
}
